import { useAuth } from "@/hooks/auth";
import {
  useCurrentUploadCommentsQuery,
  useMarkCommentsAsSeenMutation,
  usePostCurrentUploadCommentMutation,
} from "@/hooks/comments";
import Button from "@ingka/button";
import SSRIcon from "@ingka/ssr-icon";
import sortAscending from "@ingka/ssr-icon/paths/sort-ascending";
import sortDescending from "@ingka/ssr-icon/paths/sort-descending";
import Text from "@ingka/text";
import TextArea from "@ingka/text-area";
import { GetCommentDto, UploadWithAssetsDto } from "generated";
import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Comment } from "./Comment";
import { DISABLED_STATES } from "./constants";

type Props = {
  scrollHeight: string;
  upload: UploadWithAssetsDto;
  overrideOnSubmit?: () => void;
};

export const CommentPane = React.forwardRef(
  ({ scrollHeight, upload, overrideOnSubmit }: Props, ref) => {
    const { id: uploadId, state: uploadState } = upload;
    const [newComment, setNewComment] = useState("");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
    const { user } = useAuth();
    const commentsContainerRef = useRef<HTMLDivElement>(null);

    const {
      isLoading,
      isError,
      error,
      data: comments,
    } = useCurrentUploadCommentsQuery();
    const mutateMarkCommentsAsSeen =
      useMarkCommentsAsSeenMutation().mutateAsync;
    const postCurrentUploadCommentMutation =
      usePostCurrentUploadCommentMutation();

    const sortByTimestamp = (a: GetCommentDto, b: GetCommentDto) => {
      const timeA = new Date(a.createdAt).getTime();
      const timeB = new Date(b.createdAt).getTime();
      return sortOrder === "asc" ? timeA - timeB : timeB - timeA;
    };

    useEffect(() => {
      if (commentsContainerRef.current) {
        commentsContainerRef.current.scrollTop =
          commentsContainerRef.current.scrollHeight;
      }
    }, [comments]);

    useEffect(() => {
      if (comments) {
        const maxCommId = Math.max(...comments.map((comment) => comment.id));
        const markAsSeen = async () => {
          await mutateMarkCommentsAsSeen({
            uploadId,
            maxCommId,
          });
        };
        if (maxCommId > 0) {
          markAsSeen();
        }
      }
    }, [comments, uploadId, mutateMarkCommentsAsSeen]);

    const toggleSortOrder = () => {
      setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    };

    const onCommentSubmit = async () => {
      const trimComment = newComment.trim();
      if (trimComment === "") {
        return;
      }
      await postCurrentUploadCommentMutation.mutateAsync({
        createCommentDto: { uploadId, message: trimComment },
      });
      setNewComment("");
    };

    useImperativeHandle(ref, () => ({
      onCommentSubmit,
    }));

    if (isError) {
      throw error;
    }

    if (isLoading) {
      return <h1>Loading...</h1>;
    }

    return (
      <>
        <div className="flex justify-between">
          <Text tagName="h3" headingSize="s">
            Comments regarding all assets in request
          </Text>
          <SSRIcon
            className="cursor-pointer"
            onClick={toggleSortOrder}
            paths={sortOrder === "asc" ? sortDescending : sortAscending}
          />
        </div>
        <div
          className="space-y-space100 overflow-y-auto pr-[15px]"
          style={{ height: scrollHeight }}
          ref={commentsContainerRef}
        >
          {comments &&
            comments
              .sort(sortByTimestamp)
              .map((comment) => (
                <Comment
                  key={comment.id}
                  {...comment}
                  isMine={user.id === comment.userId}
                />
              ))}
        </div>
        <div className="space-y-space50">
          <TextArea
            id="comment-area"
            label="Add comment here"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={DISABLED_STATES.includes(uploadState)}
          />
          <Button
            iconPosition="leading"
            text="Comment"
            type="primary"
            onClick={overrideOnSubmit ?? onCommentSubmit}
            disabled={DISABLED_STATES.includes(uploadState)}
          />
        </div>
      </>
    );
  },
);
